'use client';

import Image from '@/components/Image';
import { useProductImages } from '@/hooks/useProductImages';
import { useStore } from '@/providers/app-store-provider';
import { EProductImageType } from '@/utils/enums/goods';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import { GoodListProduct } from './GoodList';
import { EPlatform } from '@/utils/enums/platform-enum';

export default function BannerProductCard({
  product,
  deviceType,
}: {
  product: GoodListProduct;
  deviceType: EPlatform;
}) {
  const currency = useStore((s) => s.currency);
  const userInfo = useStore((s) => s.userInfo);
  const isWeb = deviceType === EPlatform.WEB;

  const { productImage } = useProductImages({
    productData: product as any,
    imageRule: [EProductImageType.PRODUCT, EProductImageType.MATERIAL, EProductImageType.CELEBRITY],
  });

  const thirdImage =
    (product.image as any)?.product_gray?.[2] || product.image?.product?.[2] || productImage.src;

  const p = priceInfo(userInfo, product, currency) || {};
  const now = formatPriceNumberWithSymbol(String(p?.sell_price || p?.price || '0'), currency);
  const old =
    p && p.price && p.sell_price && p.sell_price !== p.price
      ? formatPriceNumberWithSymbol(String(p.price), currency)
      : '';

  return (
    <div
      className={`w-full bg-white flex items-center relative ${
        isWeb ? 'rounded-[12px] p-[12px]' : 'rounded-[6px] p-[6px]'
      }`}
      style={{ aspectRatio: isWeb ? '378/144' : '189/72' }}
    >
      {/* 图像容器 */}
      <div
        className={`bg-[#eeeeee] flex-shrink-0 flex items-center justify-center relative overflow-hidden ${
          isWeb ? 'w-[120px] h-[120px] rounded-[8px]' : 'w-[60px] h-[60px] rounded-[4px]'
        }`}
      >
        <Image
          src={thirdImage}
          alt={product.short_name || ''}
          width={isWeb ? 120 : 60}
          height={isWeb ? 120 : 60}
          className={`w-full h-auto max-h-full object-contain ${
            !productImage.isGray && productImage.type === EProductImageType.PRODUCT
              ? 'mix-blend-multiply'
              : ''
          }`}
        />
      </div>

      {/* 文本内容 */}
      <div
        className={`flex flex-col justify-between h-full min-w-0 flex-1 ${
          isWeb ? 'ml-[20px] py-[8px] pr-[40px]' : 'ml-[8px] py-[4px] pr-0'
        }`}
      >
        <p
          className={`font-br text-[#282828] leading-none truncate ${
            isWeb ? 'text-[24px]' : 'text-[14px]'
          }`}
        >
          {product.short_name}
        </p>
        <div
          className={`flex items-baseline flex-wrap mt-[auto] ${
            isWeb ? 'gap-[8px] mb-[5px]' : 'gap-[4px] mb-[2px]'
          }`}
        >
          <span
            className={`font-bs text-[#282828] leading-none notranslate ${
              isWeb ? 'text-[28px]' : 'text-[16px]'
            }`}
          >
            {now}
          </span>
          {old && (
            <span
              className={`font-br text-[#282828] line-through leading-none notranslate ${
                isWeb ? 'text-[20px]' : 'text-[12px]'
              }`}
            >
              {old}
            </span>
          )}
        </div>
      </div>

      {/* 跳转箭头按钮 */}
      <div
        className={`absolute bg-[#463ae8] rounded-full flex items-center justify-center flex-shrink-0 ${
          isWeb
            ? 'top-[12px] right-[12px] w-[40px] h-[40px]'
            : 'top-[6px] right-[6px] w-[20px] h-[20px]'
        }`}
      >
        <svg
          width={isWeb ? '24' : '12'}
          height={isWeb ? '24' : '12'}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7 17L17 7M17 7H9M17 7V15"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
}
